import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import { SortOptions } from '@usf/product-types';
import { PlatformEnum, PlatformService } from '@panamax/app-state';
import { UsfProductCardModeEnum } from '../../../shared/constants/usf-product-card-mode.enum';
import { SearchViewModel } from '../models/search-view.model';
import { SearchFiltersDesktopComponent } from '../../components/search-filters/search-filters-desktop/search-filters-desktop.component';
import { SearchFiltersMobileComponent } from '../../components/search-filters/search-filters-mobile/search-filters-mobile.component';
import { SearchFiltersTabletComponent } from '../../components/search-filters/search-filters-tablet/search-filters-tablet.component';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { FilterRequestModel } from '../models/filter-request-model';
import { Observable } from 'rxjs';
import { SearchFilterService } from '../../services/search-filter.service';
import { isValidRefinement } from '../../utils/search-filter.util';
import { ModalService } from '../../../shared/services/modal/modal.service';
import { FEATURES } from '../../../shared/constants/splitio-features';
import { SearchDeliveryToggleService } from '@search/services/search-delivery-toggle.service';
import { environment } from '../../../../environments/environment';
import { FILTER_MODAL_ACTION_TYPE } from '@search/models/search-filter.constants';
import { NewSearchViewModel } from '@app/new-search/models/new-search-view.model';
import { NewSearchFiltersMobileComponent } from '@app/new-search/components/search-filters/new-search-filters-mobile/new-search-filters-mobile.component';
import { NewSearchFiltersTabletComponent } from '@app/new-search/components/search-filters/new-search-filters-tablet/new-search-filters-tablet.component';
import { FacetFilter } from '@app/new-search/models/facet-filter.model';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-search-operations',
  templateUrl: './search-operations.component.html',
  styleUrls: ['./search-operations.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SearchOperationsComponent implements OnInit, OnDestroy {
  @Input() orientation: string;
  @Input() sortConfiguration?: SortOptions;
  @Input() searchViewModel?: SearchViewModel;
  @Input() newSearchViewModel?: NewSearchViewModel;
  @Input() selectedFacetFilters: FacetFilter[];
  @Input() showCompare: boolean;
  @Input() showAddToList: boolean;
  @Input() showInventory: boolean;
  @Input() showDeliveryToggle: boolean;
  @Input() compareButtonSelected: boolean;
  @Input() addToListSelected: boolean;
  @Input() addToInventorySelected: boolean;
  @Input() isGuestUser: boolean;
  @Output()
  selectFilterOption: EventEmitter<FilterRequestModel> = new EventEmitter();
  @Output() updateSelectedFacetFilters = new EventEmitter<FacetFilter[]>();
  @Output() toggleAddToList = new EventEmitter();
  @Output() compareProducts = new EventEmitter();
  @Output() toggleAddToInventory = new EventEmitter();

  total: number;
  total$: Observable<number>;
  platformEnum = PlatformEnum;
  usfProductCardModeEnum = UsfProductCardModeEnum;
  formObject: UntypedFormGroup;
  pageModes = {
    addToListSearch: 'add-to-list-search-mode',
    compareSearchResults: 'compare-search-results-mode',
  };
  compareButtonFeatureFlag = FEATURES.split_global_product_compare.name;

  totalSubscription: Subscription;

  constructor(
    readonly platformService: PlatformService,
    private searchFilterService: SearchFilterService,
    private modalController: ModalController,
    private formBuilder: UntypedFormBuilder,
    private modalService: ModalService,
    protected searchDeliveryToggleService: SearchDeliveryToggleService,
  ) {}

  ngOnInit() {
    if (!this.newSearchViewModel) {
      this.total$ = this.searchFilterService.getTotalFilter(
        this.searchViewModel?.searchRequest,
      );
      // Memory Leak
      this.totalSubscription = this.total$?.subscribe(t => (this.total = t));
    }
  }

  async openFilterModal() {
    if (!!this.newSearchViewModel) {
      await this.openNewSearchFilterModal();
    } else {
      await this.openSearchFilterModal();
    }
  }

  async openNewSearchFilterModal() {
    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      this.getNewSearchFilterModalComponent(),
      {
        facets: this.newSearchViewModel?.facets,
        selectedFacetFilters: this.selectedFacetFilters,
      },
      'search-filters-desktop',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal.present();

    const modalDismissResponse = await modal.onDidDismiss();

    this.handleNewSearchFilterModalDismiss(modalDismissResponse);
  }

  async openSearchFilterModal() {
    if (!this.formObject) {
      this.createForm();
    }
    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      this.getFilterModalComponent(),
      {
        selectFilterOption: this.selectFilterOption,
        searchViewModel: this.searchViewModel,
        showSort: !!this.sortConfiguration,
        selectedFilter:
          this.getDeviceType() !== PlatformEnum.mobile
            ? this.sortConfiguration?.navigationState
            : null,
        filtersFormGroup: this.formObject,
      },
      'search-filters-desktop',
    );
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal.present();

    const modalDismissResponse = await modal.onDidDismiss();
    this.handleFilterModalDismiss(modalDismissResponse);
  }

  handleFilterModalDismiss(res: any) {
    if (
      res?.data !== FILTER_MODAL_ACTION_TYPE.DESKTOP_DISMISS &&
      res?.role !== FILTER_MODAL_ACTION_TYPE.CLICK_OUTSIDE &&
      res?.role !== FILTER_MODAL_ACTION_TYPE.DRAG_DISMISS
    ) {
      const filterRequestModel: FilterRequestModel =
        this.buildFilterRequestModel(res);
      this.selectFilterOption.emit(filterRequestModel);
    }
  }

  buildFilterRequestModel(res: any): FilterRequestModel {
    if (res?.data === FILTER_MODAL_ACTION_TYPE.CONFIRM) {
      const sortOrder = !this.formObject.get('sortOptions').value.sortOption
        ? null
        : this.formObject.get('sortOptions').value.sortOption;
      let searchFilterProperties = '';
      let rangeRefinements = '';
      let selectedCategoryId = null;
      if (this.getDeviceType() === PlatformEnum.desktop) {
        searchFilterProperties =
          this.searchViewModel.searchRequest.searchFilterProperties;
        rangeRefinements = this.searchViewModel.searchRequest.rangeRefinements;
      } else {
        Object.values(this.formObject.get('singleRefinements').value).forEach(
          refinement => {
            Object.values(refinement).forEach(r => {
              if (!!r) {
                searchFilterProperties += ` ${r}`;
                selectedCategoryId = r;
              }
            });
          },
        );

        Object.values(this.formObject.get('multiRefinements').value).forEach(
          refinement => {
            Object.entries(refinement).forEach(r => {
              if (r[1] === true) {
                searchFilterProperties += ` ${r[0]}`;
              }
            });
          },
        );

        Object.values(this.formObject.get('rangeRefinements').value).forEach(
          refinement => {
            Object.entries(refinement).forEach(r => {
              if (!!r[1].minVal && !!r[1].maxVal) {
                rangeRefinements += `${r[0]}|BTWN ${r[1].minVal} ${r[1].maxVal}||`;
              } else if (!!r[1].minVal && !!r[1].oldMaxVal) {
                rangeRefinements += `${r[0]}|BTWN ${r[1].minVal} ${r[1].oldMaxVal}||`;
              } else if (!!r[1].oldMinVal && !!r[1].maxVal) {
                rangeRefinements += `${r[0]}|BTWN ${r[1].oldMinVal} ${r[1].maxVal}||`;
              } else if (!!r[1].minVal) {
                rangeRefinements += `${r[0]}|GTEQ ${r[1].minVal}||`;
              } else if (!!r[1].maxVal) {
                rangeRefinements += `${r[0]}|LTEQ ${r[1].maxVal}||`;
              }
            });
          },
        );
      }
      searchFilterProperties = searchFilterProperties?.trim() || null;
      rangeRefinements = !!rangeRefinements
        ? rangeRefinements.substring(0, rangeRefinements.length - 2)
        : null;
      let categoryId = selectedCategoryId;
      if (
        !!this.searchViewModel.searchRequest.pageType &&
        this.searchViewModel.searchRequest.pageType != 'search'
      ) {
        const categories = searchFilterProperties?.split(' ');
        const currentCategory =
          this.searchViewModel.searchRequest.searchFilterProperties?.split(
            ' ',
          )[0];
        categoryId = currentCategory;
        if (!!currentCategory && !categories?.includes(currentCategory)) {
          if (!selectedCategoryId) {
            searchFilterProperties = `${currentCategory} ${
              searchFilterProperties || ''
            }`;
          } else {
            categoryId = selectedCategoryId;
          }
          searchFilterProperties = searchFilterProperties?.trim() || null;
        }
      }

      return {
        ...this.searchViewModel.searchRequest,
        sortOrder,
        searchFilterProperties,
        rangeRefinements,
        categoryId,
      };
    } else if (res?.data === FILTER_MODAL_ACTION_TYPE.TOUCH_CLEAR) {
      let searchFilterProperties = null;
      if (
        !!this.searchViewModel.searchRequest.pageType &&
        this.searchViewModel.searchRequest.pageType != 'search'
      ) {
        searchFilterProperties =
          this.searchViewModel.searchRequest.searchFilterProperties?.split(
            ' ',
          )[0];
      }
      return {
        ...this.searchViewModel.searchRequest,
        rangeRefinements: null,
        searchFilterProperties: null,
        categoryId: null,
        rangeSelectedFilter: null,
        sortOrder: null,
        brandName: null,
      };
    }
  }

  createForm() {
    this.formObject = this.formBuilder.group({
      sortOptions: this.formBuilder.group({
        sortOption: new UntypedFormControl(
          this.searchViewModel?.searchRequest.sortOrder,
        ),
      }),
      singleRefinements: this.formBuilder.group({}),
      multiRefinements: this.formBuilder.group({}),
      rangeRefinements: this.formBuilder.group({}),
    });
    const singleRefinements = this.formObject.get(
      'singleRefinements',
    ) as UntypedFormGroup;
    const multiRefinements = this.formObject.get(
      'multiRefinements',
    ) as UntypedFormGroup;
    const rangeRefinements = this.formObject.get(
      'rangeRefinements',
    ) as UntypedFormGroup;
    this.searchViewModel?.searchResponse?.navigation?.forEach(nav => {
      if (
        nav.refinementType === 'RefinementMenu' &&
        nav.refinements[0]?.multiSelect === false
      ) {
        // Single Select Refinements
        const fg = new UntypedFormGroup({});
        const selectedValue = nav.refinements?.find(
          r => r.multiSelect === false && r.selected === true,
        )?.dimensionId;
        fg.addControl(
          `${nav.filterName}`,
          new UntypedFormControl(selectedValue),
        );
        singleRefinements.addControl(`${nav.filterName}`, fg);
      } else if (
        nav.refinementType === 'RefinementMenu' &&
        nav.refinements[0]?.multiSelect === true
      ) {
        //  Multi-Select Refinements
        multiRefinements.addControl(
          `${nav.filterName}`,
          new UntypedFormGroup({}),
        );
        const formGroup = multiRefinements.get(
          `${nav.filterName}`,
        ) as UntypedFormGroup;
        nav.refinements.forEach(r => {
          if (
            isValidRefinement(
              this.searchViewModel?.searchRequest?.searchFilterProperties,
              nav,
              r,
            )
          ) {
            formGroup.addControl(
              `${r.dimensionId}`,
              new UntypedFormControl(r.selected),
            );
          }
        });
      } else {
        // Range Refinements
        rangeRefinements.addControl(
          `${nav.displayName}`,
          new UntypedFormGroup({}),
        );
        const formGroup = rangeRefinements.get(
          `${nav.displayName}`,
        ) as UntypedFormGroup;
        nav.rangeRefinements.forEach(r => {
          formGroup.addControl(
            `${r.dimesionName}`,
            new UntypedFormGroup({
              minVal: new UntypedFormControl(),
              maxVal: new UntypedFormControl(),
              oldMinVal: new UntypedFormControl(r.minVal),
              oldMaxVal: new UntypedFormControl(r.maxVal),
            }),
          );
        });
      }
    });
  }

  getDeviceType() {
    return this.platformService.platformType;
  }

  getFilterModalComponent() {
    const deviceTypeComponent = {
      mobile: SearchFiltersMobileComponent,
      tablet: SearchFiltersTabletComponent,
      desktop: SearchFiltersDesktopComponent,
    };
    return deviceTypeComponent[this.getDeviceType()];
  }

  handleAddToListClick() {
    this.toggleAddToList.emit();
  }

  handleCompareProducts() {
    this.compareProducts.emit();
  }

  handleAddToInventoryClick() {
    this.toggleAddToInventory.emit();
  }

  getNewSearchFilterModalComponent() {
    const deviceTypeComponent = {
      mobile: NewSearchFiltersMobileComponent,
      tablet: NewSearchFiltersTabletComponent,
    };
    return deviceTypeComponent[this.getDeviceType()];
  }

  handleNewSearchFilterModalDismiss(res: any) {
    if (
      res?.role !== FILTER_MODAL_ACTION_TYPE.CLICK_OUTSIDE &&
      res?.role !== FILTER_MODAL_ACTION_TYPE.DRAG_DISMISS
    ) {
      this.updateSelectedFacetFilters.emit(res?.data);
      this.total = res?.data?.length;
    }
  }
  ngOnDestroy(): void {
    this.totalSubscription?.unsubscribe();
  }
}
